import { IconType } from '@meronex/icons/lib';
import * as React from 'react';
import {
  IconButton as IconButtonThemeUI,
  Spinner,
  IconButtonProps,
  ThemeUIStyleObject,
} from '@apia/theme';
import { icons, TIconName } from '../Icon';

export type TApiaIconButton =
  | 'icon'
  | 'icon-primary'
  | 'icon-outline'
  | 'icon-only';

export type TIconButton = IconButtonProps &
  React.AriaAttributes & {
    className?: string;
    /**
     * Es posible pasar explícitamente un ícono o el nombre del ícono de
     * acuerdo a la librería de la aplicación. Ver Icon.tsx
     */
    icon: IconType | TIconName;
    iconSize?: number | string;
    id?: string;
    disabled?: boolean;
    href?: string;
    isLoading?: boolean;
    isToggled?: boolean;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    onMouseOut?: React.MouseEventHandler<HTMLButtonElement>;
    onMouseOver?: React.MouseEventHandler<HTMLButtonElement>;
    size?: number | string;
    title?: string;
    variant?: string;
  };

const IconButton = (
  {
    icon,
    id,
    isLoading = false,
    disabled = false,
    href,
    iconSize = 'iconSm',
    onClick = () => {},
    onMouseOver = () => {},
    onMouseOut = () => {},
    size,
    title,
    isToggled = false,
    className,
    type = 'button',
    variant = 'icon',
    ...rest
  }: TIconButton,
  ref: React.Ref<HTMLButtonElement>,
) => {
  const sx: ThemeUIStyleObject = React.useMemo(
    () => ({
      width: size,
      minWidth: size,
      height: size,
      minHeight: size,
      svg: {
        width: iconSize,
        minWidth: iconSize,
        height: iconSize,
        minHeight: iconSize,
      },
    }),
    [iconSize, size],
  );

  const ActualIcon = React.useMemo(
    () => (typeof icon === 'string' ? icons[icon] : icon),
    [icon],
  );

  return (
    <IconButtonThemeUI
      id={id}
      ref={ref}
      onClick={React.useCallback(
        (ev: React.MouseEvent<HTMLButtonElement>) => {
          if (href) window.open(href, '_blank');
          onClick?.(ev);
        },
        [href, onClick],
      )}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      {...window.getVariant(variant)}
      sx={sx}
      disabled={isLoading || disabled}
      className={`${className ?? ''} iconButton ${
        isToggled ? 'isToggled' : ''
      }`}
      title={title}
      aria-label={rest.as !== undefined ? undefined : title}
      type={rest.as !== undefined ? undefined : type}
      role={href ? 'link' : undefined}
      {...rest}
    >
      {isLoading ? <Spinner color="inherit" /> : <ActualIcon />}
    </IconButtonThemeUI>
  );
};
export default React.forwardRef(IconButton);
